// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../services/api';
import type { User, AuthResponse, Team } from '../types';

interface AuthContextValue {
  user: User | null;
  teams: Team[];
  selectedTeam: Team | null;
  setSelectedTeam: React.Dispatch<React.SetStateAction<Team | null>>;
  fetchTeams: () => Promise<void>;
  login: (email: string, password: string) => Promise<AuthResponse>;
  verify2FALogin: (tempToken: string, pin: string) => Promise<AuthResponse>;
  register: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => void;
  loading: boolean;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchTeams = useCallback(async () => {
    try {
      const data = await api.getTeams();
      const nextTeams = data.teams || [];
      setTeams(nextTeams);
      // Auto-select the first team (usually personal) if none selected
      setSelectedTeam(current => current || nextTeams[0] || null);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.getMe()
        .then(data => {
          setUser(data.user);
          return fetchTeams();
        })
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchTeams]);

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    const data = await api.login(email, password);
    // If it's a standard login without 2FA, token exists
    if (data.token) {
      api.setToken(data.token);
      setUser(data.user || null);
      await fetchTeams();
    }
    return data;
  };

  const verify2FALogin = async (tempToken: string, pin: string): Promise<AuthResponse> => {
    const data = await api.verify2FALogin(tempToken, pin);
    if (data.token) {
      api.setToken(data.token);
      setUser(data.user || null);
      await fetchTeams();
    }
    return data;
  };

  const register = async (email: string, password: string): Promise<AuthResponse> => {
    const data = await api.register(email, password);
    if (data.token) {
      api.setToken(data.token);
      setUser(data.user || null);
      await fetchTeams();
    }
    return data;
  };

  const logout = (): void => {
    api.setToken(null);
    setUser(null);
    setTeams([]);
    setSelectedTeam(null);
  };

  return (
    <AuthContext.Provider value={{ user, teams, selectedTeam, setSelectedTeam, fetchTeams, login, verify2FALogin, register, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
